// Board-Sharing UI (P4). Owner-only Add/Remove, Member-Liste fuer alle Member.
// Kandidaten-Liste kommt aus /api/users (provisionierte DB-User, demo + entra).

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import { type DemoUser, authApi } from "../auth/api.js";
import { type MemberDto, boardsApi } from "../board-list/api.js";
import "./ShareModal.css";

interface ShareModalProps {
  open: boolean;
  boardId: string;
  ownerId: string;
  onClose: () => void;
}

export function ShareModal({
  open,
  boardId,
  ownerId,
  onClose,
}: ShareModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [candidates, setCandidates] = useState<DemoUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickedUserId, setPickedUserId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const isOwner = user?.id === ownerId;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [m, u] = await Promise.all([boardsApi.listMembers(boardId), authApi.listUsers()]);
      setMembers(m);
      setCandidates(u.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  const memberIds = new Set(members.map((m) => m.userId));
  const addable = candidates.filter((u) => !memberIds.has(u.id));

  const searchLower = search.toLowerCase().trim();
  const filtered = searchLower
    ? addable.filter(
        (u) =>
          u.displayName.toLowerCase().includes(searchLower) ||
          u.email.toLowerCase().includes(searchLower),
      )
    : addable;

  useEffect(() => {
    if (filtered.length === 0) {
      setPickedUserId("");
      return;
    }
    if (!pickedUserId || !filtered.some((u) => u.id === pickedUserId)) {
      setPickedUserId(filtered[0]?.id ?? "");
    }
  }, [filtered, pickedUserId]);

  const onAdd = async (): Promise<void> => {
    if (!pickedUserId) return;
    setBusyUserId(pickedUserId);
    setError(null);
    try {
      const m = await boardsApi.addMember(boardId, pickedUserId);
      setMembers((prev) => [...prev, m]);
      setSearch("");
      setPickerOpen(false);
    } catch (err) {
      setError(`${t("share.error.add")}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyUserId(null);
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const idx = filtered.findIndex((u) => u.id === pickedUserId);
      const next = filtered[Math.min(idx + 1, filtered.length - 1)];
      if (next) setPickedUserId(next.id);
      setPickerOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const idx = filtered.findIndex((u) => u.id === pickedUserId);
      const prev = filtered[Math.max(idx - 1, 0)];
      if (prev) setPickedUserId(prev.id);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (pickedUserId && busyUserId === null) void onAdd();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setPickerOpen(false);
    }
  };

  const onRemove = async (userId: string): Promise<void> => {
    setBusyUserId(userId);
    setError(null);
    try {
      await boardsApi.removeMember(boardId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch (err) {
      setError(`${t("share.error.remove")}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <dialog ref={dialogRef} className="share-modal-dialog" onClose={onClose}>
      <form method="dialog" className="share-modal-form">
        <header className="share-modal-header">
          <h2>{t("share.modal.title")}</h2>
          <button
            type="button"
            className="share-modal-close"
            onClick={onClose}
            aria-label={t("share.modal.close")}
          >
            ×
          </button>
        </header>
        {error && (
          <p className="share-modal-error" role="alert">
            {error}
          </p>
        )}

        <section className="share-modal-section">
          <h3 className="share-modal-section-title">{t("share.modal.members")}</h3>
          {loading ? (
            <p className="share-modal-muted">{t("share.loading")}</p>
          ) : members.length === 0 ? (
            <p className="share-modal-muted">{t("share.empty")}</p>
          ) : (
            <ul className="share-member-list">
              {members.map((m) => {
                const isSelf = m.userId === user?.id;
                const canRemove = isOwner && m.role !== "owner" && !isSelf;
                return (
                  <li key={m.userId} className="share-member-item">
                    <div className="share-member-info">
                      <div className="share-member-name">
                        {m.displayName}
                        {isSelf && <span className="share-member-self"> ({t("share.you")})</span>}
                      </div>
                      <div className="share-member-email">{m.email}</div>
                    </div>
                    <span className={`share-role-badge share-role-${m.role}`}>
                      {t(`share.role.${m.role}`)}
                    </span>
                    {canRemove && (
                      <button
                        type="button"
                        className="share-member-remove"
                        disabled={busyUserId === m.userId}
                        onClick={() => onRemove(m.userId)}
                      >
                        {t("share.remove")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {isOwner && (
          <section className="share-modal-section">
            <h3 className="share-modal-section-title">{t("share.modal.addUser")}</h3>
            {addable.length === 0 ? (
              <p className="share-modal-muted">{t("share.noOtherUsers")}</p>
            ) : (
              <div className="share-combobox">
                <div className="share-combobox-row">
                  <input
                    type="text"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    aria-controls="share-combobox-list"
                    aria-autocomplete="list"
                    className="share-combobox-input"
                    value={search}
                    placeholder={t("share.search.placeholder")}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPickerOpen(true);
                    }}
                    onFocus={() => setPickerOpen(true)}
                    onBlur={() => {
                      // Delay damit Click auf Listenpunkt nicht durch blur abgewuergt wird.
                      window.setTimeout(() => setPickerOpen(false), 120);
                    }}
                    onKeyDown={onSearchKeyDown}
                    disabled={busyUserId !== null}
                  />
                  <button
                    type="button"
                    className="share-add-button"
                    disabled={!pickedUserId || busyUserId !== null}
                    onClick={onAdd}
                  >
                    {t("share.modal.add")}
                  </button>
                </div>
                {pickerOpen && (
                  <div id="share-combobox-list" className="share-combobox-list">
                    {filtered.length === 0 ? (
                      <div className="share-combobox-empty">{t("share.search.noMatches")}</div>
                    ) : (
                      filtered.slice(0, 12).map((u) => {
                        const selected = u.id === pickedUserId;
                        return (
                          <button
                            type="button"
                            key={u.id}
                            data-selected={selected}
                            className={`share-combobox-option ${selected ? "is-selected" : ""}`}
                            // onMouseDown statt onClick: feuert vor blur des Inputs,
                            // sonst schliesst der Blur-Timer die Liste bevor der Klick durch ist.
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setPickedUserId(u.id);
                              setSearch(u.displayName);
                              setPickerOpen(false);
                            }}
                          >
                            <div className="share-combobox-name">{u.displayName}</div>
                            <div className="share-combobox-email">{u.email}</div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
            <p className="share-modal-hint">{t("share.modal.hint")}</p>
          </section>
        )}
      </form>
    </dialog>
  );
}
